import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, MapPin, GraduationCap, Sparkle, ArrowRight, Certificate,
  Users, PenNib,
} from "@phosphor-icons/react";

const LEVEL_LABEL = {
  expert: "NaturePulse Expert",
  trusted: "Trusted Expert",
  verified: "Verified Expert",
  institutional: "Institutional Expert",
  fellow: "NaturePulse Fellow",
};

const SORTS = [
  { key: "recent", label: "Most Recent" },
  { key: "followers", label: "Most Followed" },
  { key: "posts", label: "Most Active" },
  { key: "az", label: "A → Z" },
];

const ExpertCard = ({ e }) => (
  <Link
    to={`/expert/${e.slug}`}
    data-testid={`expert-card-${e.slug}`}
    className="group block rounded-2xl border border-black/10 bg-white p-5 shadow-sm hover:shadow-md hover:border-[color:var(--np-brand,#B85F2A)] transition"
  >
    <div className="flex items-start gap-4">
      <div className="w-16 h-16 rounded-full bg-[#F5EBDC] overflow-hidden grid place-items-center flex-shrink-0">
        {e.profile_photo ? (
          <img src={e.profile_photo} alt={e.display_name || e.name} className="w-full h-full object-cover" />
        ) : (
          <PenNib size={22} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-serif-display text-lg leading-tight">{e.display_name || e.name}</span>
          {e.verified && (
            <span title="Verified" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 px-1.5 py-[1px] font-mono-tech text-[9px] tracking-[0.24em] uppercase">
              <Certificate size={9} weight="fill" /> Verified
            </span>
          )}
        </div>
        {e.professional_title && (
          <div className="mt-0.5 text-xs text-[#3A3830] line-clamp-1">{e.professional_title}</div>
        )}
        {e.institution && (
          <div className="text-[10px] text-[#3A3830] flex items-center gap-1 mt-1">
            <GraduationCap size={10} weight="fill" /> {e.institution}
          </div>
        )}
        {(e.location?.city || e.location?.country) && (
          <div className="text-[10px] text-[#3A3830] flex items-center gap-1 mt-0.5">
            <MapPin size={10} weight="fill" /> {[e.location.city, e.location.state, e.location.country].filter(Boolean).join(", ")}
          </div>
        )}
        {e.short_bio && (
          <p className="mt-2 text-xs text-[#3A3830] line-clamp-3">{e.short_bio}</p>
        )}
        {e.expertise?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {e.expertise.slice(0, 4).map((t) => (
              <span key={t} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#241209]">{t}</span>
            ))}
          </div>
        )}
      </div>
    </div>
    <div className="mt-4 flex items-center justify-between font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
      <span>{LEVEL_LABEL[e.level] || "NaturePulse Expert"}</span>
      <span className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        View space <ArrowRight size={11} weight="bold" />
      </span>
    </div>
  </Link>
);

export default function ExpertsDirectory() {
  const [params, setParams] = useSearchParams();
  const [experts, setExperts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ countries: [], languages: [], topics: [] });
  const q = params.get("q") || "";
  const topic = params.get("topic") || "";
  const country = params.get("country") || "";
  const language = params.get("language") || "";
  const level = params.get("level") || "";
  const sort = params.get("sort") || "recent";

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    http.get("/experts/filters").then(({ data }) => setFilters(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    http.get("/experts", { params: { q, topic, country, language, level, sort, limit: 24 } })
      .then(({ data }) => { setExperts(data.experts || []); setTotal(data.total || 0); })
      .catch(() => toast.error("Could not load experts"))
      .finally(() => setLoading(false));
  }, [q, topic, country, language, level, sort]);

  const heroDescription = useMemo(() => (
    "Scientists, farmers, birdwatchers, teachers and long-time observers — the NaturePulse Expert Space is where trusted voices publish articles, share research, answer questions and build communities around nature."
  ), []);

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="experts-directory">
      <div className="max-w-[1200px] mx-auto">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          / NaturePulse Experts
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
          Experts sharing knowledge. Communities observing nature.
        </h1>
        <p className="mt-3 max-w-3xl font-serif-display text-base md:text-lg italic text-[#3A3830]">
          {heroDescription}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/experts/apply"
            data-testid="experts-apply-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
          >
            <Sparkle size={12} weight="fill" /> Apply to become a NaturePulse Expert
          </Link>
          <Link
            to="/experts/research-requests"
            className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-black/5"
            data-testid="experts-research-requests-cta"
          >
            <Users size={12} weight="fill" /> Research requests · help science
          </Link>
        </div>

        {/* Search + filters */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
          <div>
            <div className="flex items-center gap-2 rounded-full bg-white border border-black/15 px-4 py-2.5 shadow-sm">
              <MagnifyingGlass size={16} weight="bold" className="text-[#3A3830]" />
              <input
                value={q}
                onChange={(e) => setParam("q", e.target.value)}
                placeholder="Search by name, topic, profession or expertise…"
                data-testid="experts-search"
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setParam("sort", s.key)}
                  data-testid={`experts-sort-${s.key}`}
                  className={`rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase border ${
                    sort === s.key
                      ? "border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white"
                      : "border-black/15 bg-white hover:bg-black/5 text-[#241209]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="experts-grid">
              {loading && Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-black/10 bg-white/60 p-5 h-40 animate-pulse" />
              ))}
              {!loading && experts.length === 0 && (
                <div className="col-span-full text-center py-10 text-sm text-[#3A3830]">
                  No experts found yet. Be the first — <Link to="/experts/apply" className="text-[color:var(--np-brand,#B85F2A)] underline">apply here</Link>.
                </div>
              )}
              {!loading && experts.map((e) => <ExpertCard key={e.id} e={e} />)}
            </div>
            {experts.length > 0 && (
              <div className="mt-3 text-[10px] font-mono-tech tracking-[0.24em] uppercase text-[#3A3830]">
                Showing {experts.length} of {total} experts
              </div>
            )}
          </div>

          <aside className="space-y-5 lg:sticky lg:top-24 h-fit">
            <FilterList label="Topic" activeKey={topic} onPick={(v) => setParam("topic", v)} rows={filters.topics} />
            <FilterList label="Country" activeKey={country} onPick={(v) => setParam("country", v)} rows={filters.countries} />
            <FilterList label="Language" activeKey={language} onPick={(v) => setParam("language", v)} rows={filters.languages} />
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">Level</div>
              <div className="flex flex-wrap gap-1.5">
                {["expert", "trusted", "verified", "institutional", "fellow"].map((L) => (
                  <button
                    key={L}
                    onClick={() => setParam("level", level === L ? "" : L)}
                    data-testid={`experts-level-${L}`}
                    className={`rounded-full px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase border ${
                      level === L
                        ? "border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white"
                        : "border-black/15 bg-white text-[#241209] hover:bg-black/5"
                    }`}
                  >
                    {LEVEL_LABEL[L]}
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

const FilterList = ({ label, activeKey, onPick, rows }) => (
  <div>
    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">{label}</div>
    <div className="space-y-1.5 max-h-56 overflow-auto pr-1">
      {(rows || []).slice(0, 20).map((r) => (
        <button
          key={r.key}
          onClick={() => onPick(activeKey === r.key ? "" : r.key)}
          className={`w-full text-left flex items-center justify-between rounded-lg px-2 py-1.5 text-xs ${
            activeKey === r.key
              ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
              : "bg-white/70 hover:bg-white text-[#241209]"
          }`}
          data-testid={`experts-filter-${label.toLowerCase()}-${r.key}`}
        >
          <span className="truncate">{r.key}</span>
          <span className="opacity-70">{r.count}</span>
        </button>
      ))}
      {(!rows || rows.length === 0) && (
        <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">No data yet</div>
      )}
    </div>
  </div>
);
